import { useRef, useState } from 'react';
import { useParams } from 'react-router';

import { ApiError, errorMessage } from '@/api/client';
import { useConfigHistory, useRevertConfig } from '@/api/queries';
import { NotFoundPage } from '@/app/errors';
import { configPath, useScopeParam } from '@/app/scope';
import { useToast } from '@/app/toasts';
import { Tag } from '@/components/badges';
import { Button, LinkButton } from '@/components/Button';
import { useFocusFirstInvalid } from '@/components/focus';
import { Dialog } from '@/components/Dialog';
import { DIALOG_LINGER_MS, useLingering } from '@/components/motion';
import { EmptyState, ErrorNotice, Loading, Panel, Table, Td, Th } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import type { ConfigHistoryEntry, Scope } from '@/domain/types';
import { ApproverField, ErrorSummary } from '@/features/proposals/decision';
import { explainError, useApproverName } from '@/features/proposals/decisionState';

import { AUDIT_FIELDS, formatValue, labelFor } from './helpers';
import { PageHeader, ReasonField } from './shared';

export function ConfigHistoryPage() {
  const scope = useScopeParam();
  const { capabilityId } = useParams();
  if (!scope || !capabilityId) return <NotFoundPage />;
  return <ConfigHistory scope={scope} capabilityId={capabilityId} />;
}

function ConfigHistory({ scope, capabilityId }: { scope: Scope; capabilityId: string }) {
  const history = useConfigHistory(scope, capabilityId);
  const [reverting, setReverting] = useState<ConfigHistoryEntry | null>(null);
  // The dialog is mounted per entry (fresh fields each time) and lingers, closed, to fade out.
  const dialogFor = useLingering(reverting, DIALOG_LINGER_MS);
  const newest = history.data?.[0];
  const displayName = newest ? newest.after.display_name : capabilityId;
  const editPath = configPath(scope, capabilityId);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        breadcrumb={[
          { label: 'Configuration', to: configPath(scope) },
          { label: displayName, to: editPath },
          { label: 'History' },
        ]}
        title={`History of ${displayName}`}
        actions={<LinkButton to={editPath}>Edit the current version</LinkButton>}
      />

      {history.isPending ? <Loading label="Loading history…" /> : null}
      {history.isError ? (
        history.error instanceof ApiError && history.error.status === 404 ? (
          <NotFoundPage />
        ) : (
          <ErrorNotice
            title="The history could not be loaded"
            message={errorMessage(history.error)}
            action={
              <Button size="sm" onClick={() => void history.refetch()}>
                Try again
              </Button>
            }
          />
        )
      ) : null}

      {history.data && history.data.length === 0 ? (
        <Panel bodyClassName="p-0">
          <EmptyState title="No history yet" compact />
        </Panel>
      ) : null}

      {history.data && history.data.length > 0 ? (
        <ol className="flex flex-col gap-4 m-0 p-0 list-none">
          {history.data.map((entry, index) => (
            <li key={entry.version}>
              <HistoryEntry
                entry={entry}
                isCurrent={index === 0}
                onRevert={() => setReverting(entry)}
              />
            </li>
          ))}
        </ol>
      ) : null}

      {dialogFor ? (
        <RevertDialog
          key={dialogFor.version}
          scope={scope}
          capabilityId={capabilityId}
          entry={dialogFor}
          open={reverting !== null}
          onClose={() => setReverting(null)}
        />
      ) : null}
    </div>
  );
}

function HistoryEntry({
  entry,
  isCurrent,
  onRevert,
}: {
  entry: ConfigHistoryEntry;
  isCurrent: boolean;
  onRevert: () => void;
}) {
  const before = (entry.before ?? {}) as Record<string, unknown>;
  const after = entry.after as unknown as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
    (key) => !AUDIT_FIELDS.has(key),
  );
  const changed = entry.before
    ? keys.filter((key) => formatValue(key, before[key]) !== formatValue(key, after[key]))
    : keys;

  return (
    <Panel
      as="article"
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span className="tabular">Version {Number(entry.version)}</span>
          {isCurrent ? <Tag tone="brand">Current</Tag> : null}
          {!entry.before ? (
            <span className="text-body font-normal text-ink-3">Initial configuration</span>
          ) : null}
        </span>
      }
      description={
        <>
          <Timestamp iso={entry.changed_at} mode="full" /> · {entry.changed_by}
        </>
      }
      actions={
        !isCurrent ? (
          <Button size="sm" onClick={onRevert}>
            Revert to version {Number(entry.version)}…
          </Button>
        ) : undefined
      }
      bodyClassName="p-0"
    >
      <p className="px-5 py-3 text-body border-b border-line">
        <span className="text-ink-3">Reason · </span>
        {entry.reason}
      </p>
      {changed.length === 0 ? (
        <EmptyState title="No field changed in this version" compact />
      ) : (
        <Table className="[&>tbody>tr:last-child>td]:border-b-0">
          <thead>
            <tr>
              <Th className="w-[220px] pl-5">Field</Th>
              {entry.before ? <Th>Before</Th> : null}
              <Th className="pr-5">{entry.before ? 'After' : 'Value'}</Th>
            </tr>
          </thead>
          <tbody>
            {changed.map((key) => (
              <tr key={key}>
                <Td className="pl-5 text-ink-2">{labelFor(key)}</Td>
                {entry.before ? (
                  <Td className="break-words text-ink-3">{formatValue(key, before[key])}</Td>
                ) : null}
                <Td className="pr-5 break-words font-medium">{formatValue(key, after[key])}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Panel>
  );
}

function RevertDialog({
  scope,
  capabilityId,
  entry,
  open,
  onClose,
}: {
  scope: Scope;
  capabilityId: string;
  entry: ConfigHistoryEntry;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);
  const [message, setMessage] = useState<string | null>(null);
  const mutation = useRevertConfig(scope, capabilityId);
  const toast = useToast();
  const version = Number(entry.version);

  const close = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    const local: Record<string, string> = {};
    if (!reason.trim()) local.reason = 'Give a reason.';
    if (!approver.name.trim()) local.changed_by = 'Say who is making the change.';
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }
    approver.remember();
    try {
      const { config } = await mutation.mutateAsync({
        to_version: version,
        changed_by: approver.name.trim(),
        reason: reason.trim(),
      });
      toast.push({
        tone: 'success',
        title: `Reverted to version ${version}, saved as version ${config.version}`,
      });
      onClose();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setMessage('A newer version was saved meanwhile. Check it before reverting again.');
        return;
      }
      const explained = explainError(error);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={`Revert to version ${version}`}
      description={`The values of version ${version} are saved as a new version and apply at once.`}
      busy={mutation.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="revert-form" loading={mutation.isPending}>
            Revert to version {version}
          </Button>
        </>
      }
    >
      <form
        ref={formRef}
        id="revert-form"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <ReasonField
          value={reason}
          onChange={setReason}
          error={fieldErrors.reason}
          disabled={mutation.isPending}
          placeholder="e.g. The tighter ladder proposed too often."
        />
        <ApproverField
          label="Changed by"
          value={approver.name}
          onChange={approver.setName}
          error={fieldErrors.changed_by}
          disabled={mutation.isPending}
        />
        <ErrorSummary message={message} />
      </form>
    </Dialog>
  );
}
